import { Pencil, Plus, ScrollText, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@bigpp/ui/components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bigpp/ui/components/popover";
import { SkillGroupHeader, SkillRow } from "@bigpp/ui/components/skill-row";
import { Tooltip } from "@bigpp/ui/components/tooltip";

import { trpc } from "../trpc.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { SkillEditorDialog } from "./SkillEditorDialog.tsx";
import { BRAND } from "@bigpp/config/brand";

type Props = {
  pinnedSkillIds: string[];
  setPinnedSkillIds: (ids: string[]) => void;
  disabled?: boolean;
};

export function SkillsPopover({ pinnedSkillIds, setPinnedSkillIds, disabled }: Props) {
  const utils = trpc.useUtils();
  const skills = trpc.skills.list.useQuery();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [confirmTarget, setConfirmTarget] = useState<
    { id: string; name: string } | null
  >(null);

  const del = trpc.skills.delete.useMutation({
    onSuccess: () => {
      void utils.skills.list.invalidate();
      void utils.runs.listSkills.invalidate();
      // Clear pinned reference to the deleted skill.
      if (confirmTarget) {
        setPinnedSkillIds(pinnedSkillIds.filter((x) => x !== confirmTarget.id));
      }
      setConfirmTarget(null);
    },
  });

  const toggle = (id: string) => {
    if (pinnedSkillIds.includes(id)) {
      setPinnedSkillIds(pinnedSkillIds.filter((x) => x !== id));
    } else {
      setPinnedSkillIds([...pinnedSkillIds, id]);
    }
  };

  const openNew = () => {
    setEditingId(undefined);
    setEditorOpen(true);
  };
  const openEdit = (id: string) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  const systemSkills = skills.data?.filter((s) => s.kind === "system") ?? [];
  const userSkills = skills.data?.filter((s) => s.kind === "user") ?? [];

  return (
    <>
      <Popover>
        <Tooltip label="Skills">
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Skills"
              disabled={disabled}
            >
              <ScrollText />
              {pinnedSkillIds.length > 0 && (
                <span className="text-[10px] font-semibold text-[var(--color-primary)]">
                  {pinnedSkillIds.length.toString()}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent align="start" side="top" className="w-80 p-0">
          <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Skills</div>
              <p className="mt-0.5 text-[11px] leading-snug text-[var(--color-muted-foreground)]">
                Check to <strong>force-activate</strong>
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={openNew}
              className="shrink-0 h-7 px-2 text-xs"
            >
              <Plus />
              New
            </Button>
          </div>
          {/* Radix ScrollArea + max-h clips without scrolling in the Office webview. */}
          <div className="max-h-[320px] overflow-y-auto overscroll-contain px-3 pb-3">
            <div className="flex flex-col gap-3">
              {skills.isLoading && (
                <div className="text-xs text-[var(--color-muted-foreground)]">Loading…</div>
              )}
              {skills.error && (
                <div className="text-xs text-[var(--color-destructive)]">
                  {skills.error.message}
                </div>
              )}
              {skills.data && skills.data.length === 0 && (
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  No skills yet. Click <strong>New</strong> to write your first.
                </div>
              )}

              <SkillGroup
                label="Your skills"
                emptyHint="None yet."
                footer={
                  <p className="text-[11px] leading-snug text-[var(--color-muted-foreground)] px-1.5 pt-0.5">
                    Tip: you can ask {BRAND} to write a skill for you.
                  </p>
                }
                rows={userSkills}
                pinnedIds={pinnedSkillIds}
                onToggle={toggle}
                onEdit={openEdit}
                onDelete={(id, name) => {
                  setConfirmTarget({ id, name });
                }}
                editable
              />

              <SkillGroup
                label="System"
                emptyHint=""
                rows={systemSkills}
                pinnedIds={pinnedSkillIds}
                onToggle={toggle}
                editable={false}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <SkillEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        skillId={editingId}
      />

      <ConfirmDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmTarget(null);
        }}
        title="Delete this skill?"
        description={
          confirmTarget
            ? `"${confirmTarget.name}" will be removed permanently. This cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        pending={del.isPending}
        onConfirm={() => {
          if (confirmTarget) del.mutate({ id: confirmTarget.id });
        }}
      />
    </>
  );
}

type Row = {
  id: string;
  kind: string;
  slug: string;
  name: string;
  description: string;
};

function SkillGroup({
  label,
  emptyHint,
  footer,
  rows,
  pinnedIds,
  onToggle,
  onEdit,
  onDelete,
  editable,
}: {
  label: string;
  emptyHint: string;
  footer?: React.ReactNode;
  rows: Row[];
  pinnedIds: string[];
  onToggle: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string, name: string) => void;
  editable: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <SkillGroupHeader label={label} />
      {rows.length === 0 && emptyHint && (
        <div className="text-[11px] text-[var(--color-muted-foreground)] px-1.5 pb-1">
          {emptyHint}
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        {rows.map((s) => (
          <SkillRow
            key={s.id}
            skill={{ name: s.name, description: s.description, slug: s.slug }}
            pinned={pinnedIds.includes(s.id)}
            onToggle={() => {
              onToggle(s.id);
            }}
            actions={
              editable && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  {onEdit && (
                    <button
                      type="button"
                      aria-label={`Edit ${s.name}`}
                      onClick={() => {
                        onEdit(s.id);
                      }}
                      className="p-1 rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-background)] hover:text-[var(--color-foreground)]"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      aria-label={`Delete ${s.name}`}
                      onClick={() => {
                        onDelete(s.id, s.name);
                      }}
                      className="p-1 rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-background)] hover:text-[var(--color-destructive)]"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
              )
            }
          />
        ))}
      </div>
      {footer}
    </div>
  );
}
